/**
 * Webhooks guide — registering endpoints for run event notifications.
 *
 * Covers webhook registration, event types, payload format, signature
 * verification, and delivery management.
 */

export const webhooksGuideContent = `
> **These are outbound webhook events** — your server gets notified when runs complete. For **inbound** webhooks that start runs, see [Webhook Triggers](/docs/webhook-triggers).

Register webhook endpoints to receive real-time notifications when runs complete, fail, or need input. Events are delivered as signed HTTP POST requests with automatic retries.

## Register a Webhook

\`\`\`python
from m8tes import M8tes

client = M8tes()

webhook = client.webhooks.create(
    url="https://your-app.com/webhooks/m8tes",
    events=["run.completed", "run.failed"]
)

# Save the secret — only returned on creation
print(webhook.secret)  # "a1b2c3d4e5f6..."
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/webhooks \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://your-app.com/webhooks/m8tes", "events": ["run.completed", "run.failed"]}'
\`\`\`

> **Important:** The \`secret\` is only shown once, on creation. Store it securely for signature verification.

## Available Events

| Event | Description | Default |
|-------|-------------|---------|
| \`run.started\` | A run began execution (fires on every attempt, including replies/resumes) | No |
| \`run.completed\` | A run finished successfully | Yes |
| \`run.failed\` | A run encountered an error or was cancelled | Yes |
| \`run.awaiting_input\` | A run is waiting for user input or approval | No |

## Payload Format

\`\`\`json
{
  "id": "evt_abc123",
  "type": "run.completed",
  "created_at": "2026-01-15T10:05:00Z",
  "data": {
    "id": 42,
    "teammate_id": 1,
    "status": "completed",
    "output": "Done! Closed 5 tickets.",
    "error": null,
    "user_id": null
  }
}
\`\`\`

## Signature Verification

Each request includes \`Webhook-Id\`, \`Webhook-Timestamp\`, and \`Webhook-Signature\` headers. Use the SDK helper to verify:

\`\`\`python
from m8tes import Webhooks

is_valid = Webhooks.verify_signature(
    body=request.body,
    headers=request.headers,
    secret=your_webhook_secret,
    tolerance_seconds=300,  # reject payloads older than 5 minutes
)
\`\`\`

## Manage Webhooks

\`\`\`python
# List all registered webhooks (secrets are masked)
page = client.webhooks.list()
for wh in page.data:
    print(wh.url, wh.events, wh.active)

# Update events or disable
client.webhooks.update(webhook.id, events=["run.started", "run.completed"])
client.webhooks.update(webhook.id, active=False)

# View delivery history
deliveries = client.webhooks.list_deliveries(webhook.id)
for d in deliveries.data:
    print(d.event_type, d.status, d.attempts)

# Delete a webhook
client.webhooks.delete(webhook.id)
\`\`\`

\`\`\`bash
# List webhooks
curl https://m8tes.ai/api/v2/webhooks \\
  -H "Authorization: Bearer m8_your_key_here"

# Update a webhook
curl -X PATCH https://m8tes.ai/api/v2/webhooks/2 \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"events": ["run.started", "run.completed"]}'

# View deliveries
curl https://m8tes.ai/api/v2/webhooks/2/deliveries \\
  -H "Authorization: Bearer m8_your_key_here"

# Delete a webhook
curl -X DELETE https://m8tes.ai/api/v2/webhooks/2 \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

## Retry Behavior

Delivery is attempted up to 3 times total with exponential backoff between attempts (2s, 4s delay). A delivery is considered successful on any 2xx response. After all attempts are exhausted, the delivery is marked as failed and visible in the delivery log via \`client.webhooks.list_deliveries()\`. Pending deliveries are also retried automatically if the server restarts.

## What's Next

- [Webhook Triggers](/docs/webhook-triggers) — inbound webhooks that start runs
- [Streaming & Events](/docs/streaming) — consume run events in real-time via SSE
- [Scheduling](/docs/scheduling) — automate runs with cron and interval triggers
`;
